import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';
import { UserManagement } from './UserManagement';
import { Roles } from './Roles';
import { Permissions } from './Permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { hasPermission } from '../../types';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, MailIcon, UsersIcon, KeyIcon, ShieldCheckIcon, CameraIcon, EyeIcon, ChevronRightIcon } from 'lucide-react';
interface NotificationSettings {
  newAppointments: boolean;
  cancelledAppointments: boolean;
  newPatients: boolean;
}
interface Preferences {
  darkMode: boolean;
  desktopNotifications: boolean;
}
function ReadOnlyBanner() {
  return <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center gap-3 text-sm shadow-sm" data-id="element-2585">
      <EyeIcon className="w-5 h-5 flex-shrink-0" data-id="element-2586" />
      <span className="font-medium" data-id="element-2587">
        Solo lectura — No tienes permisos para editar esta sección.
      </span>
    </div>;
}
export function Settings() {
  const {
    user,
    updateUser,
    login
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const userRole = user?.role || '';
  // Permission checks
  const canEditProfile = hasPermission(userRole, 'settings_profile', 'full');
  const canEditPreferences = hasPermission(userRole, 'settings_preferences', 'full');
  const canEditNotifications = hasPermission(userRole, 'settings_notifications', 'full');
  const canEditSecurity = hasPermission(userRole, 'settings_security', 'full');
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaved, setProfileSaved] = useState(false);
  const [avatarSaved, setAvatarSaved] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sync local state when user changes in context (e.g., updated from another place)
  useEffect(() => {
    if (user?.name && user.name !== name && !profileSaved) {
      setName(user.name);
    }
    if (user?.email && user.email !== email && !profileSaved) {
      setEmail(user.email);
    }
  }, [user?.name, user?.email]);
  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Preferences state
  const [preferences, setPreferences] = useState<Preferences>({
    darkMode: theme === 'dark',
    desktopNotifications: false
  });
  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newAppointments: true,
    cancelledAppointments: true,
    newPatients: true
  });
  // Active Tab State
  const [activeTabId, setActiveTabId] = useState('profile');
  // Load preferences
  useEffect(() => {
    const savedPrefs = localStorage.getItem('savidna-preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
      } catch (e) {
        console.error('Error loading preferences:', e);
      }
    }
    const savedNotifs = localStorage.getItem('savidna-notification-settings');
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
      } catch (e) {
        console.error('Error loading notification settings:', e);
      }
    }
  }, []);
  // Save preferences
  const savePreferences = (newPrefs: Preferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('savidna-preferences', JSON.stringify(newPrefs));
  };
  // Save notification settings
  const saveNotifications = (newNotifs: NotificationSettings) => {
    setNotifications(newNotifs);
    localStorage.setItem('savidna-notification-settings', JSON.stringify(newNotifs));
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande. El tamaño máximo es 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPendingAvatar(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSaveAvatar = () => {
    if (!pendingAvatar) return;
    if (updateUser) {
      updateUser({
        avatar: pendingAvatar
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          avatar: pendingAvatar
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        console.error('Error saving avatar:', e);
      }
    }
    setPendingAvatar(null);
    setAvatarSaved(true);
    setTimeout(() => setAvatarSaved(false), 3000);
  };
  const handleSaveProfile = () => {
    if (!canEditProfile) return;
    if (!name || !email) {
      alert('Por favor completa todos los campos');
      return;
    }
    if (updateUser) {
      updateUser({
        name,
        email
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          name,
          email
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        console.error('Error updating user:', e);
      }
    }
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };
  const handleChangePassword = async () => {
    if (!canEditSecurity) return;
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setIsChangingPassword(true);
    try {
      if (!user?.email) {
        setPasswordError('Error de sesión. Recarga la página.');
        setIsChangingPassword(false);
        return;
      }
      const loginResult = await login(user.email, currentPassword);
      if (!loginResult.success) {
        setPasswordError('La contraseña actual es incorrecta');
        setIsChangingPassword(false);
        return;
      }
      await updateUser({
        password: newPassword
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (e) {
      console.error('Error updating password:', e);
      setPasswordError('Error al cambiar la contraseña');
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handleToggleDarkMode = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      darkMode: checked
    });
    toggleTheme();
  };
  const handleToggleDesktopNotifications = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      desktopNotifications: checked
    });
    if (checked && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Notificaciones activadas', {
            body: 'Ahora recibirás notificaciones de Savidna',
            icon: 'https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg'
          });
        }
      });
    }
  };
  // Define tabs content
  const allTabs = [{
    id: 'profile',
    label: 'Perfil',
    icon: UserIcon,
    permissionKey: 'settings_profile' as const,
    content: <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2588">
          {!canEditProfile && <ReadOnlyBanner data-id="element-2589" />}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2590">
            <div className="px-8 py-8" data-id="element-2591">
              <div className="relative flex flex-col sm:flex-row justify-between items-start mb-6 gap-4" data-id="element-2592">
                <div className="flex items-center gap-6" data-id="element-2593">
                  <div className="relative group" data-id="element-2594">
                    <div className="w-24 h-24 rounded-full border-4 border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-700 overflow-hidden relative shadow-md cursor-pointer" onClick={handleAvatarClick} data-id="element-2595">
                      {pendingAvatar || user?.avatar ? <img src={pendingAvatar || user?.avatar} alt={user?.name || ''} className="w-full h-full object-cover" data-id="element-2596" /> : <div className="w-full h-full flex items-center justify-center bg-teal-50 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 text-3xl font-bold" data-id="element-2597">
                          {name.charAt(0).toUpperCase()}
                        </div>}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-2598">
                        <CameraIcon className="w-8 h-8 text-white" data-id="element-2599" />
                      </div>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} data-id="element-2600" />
                  </div>
                  <div data-id="element-2601">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2602">
                      {name}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400" data-id="element-2603">{email}</p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2" data-id="element-2604">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800 capitalize" data-id="element-2605">
                    {user?.role || 'Usuario'}
                  </span>
                  {pendingAvatar && <Button onClick={handleSaveAvatar} size="sm" data-id="element-2606">
                      <CameraIcon className="w-4 h-4 mr-1" data-id="element-2607" />
                      Guardar Foto
                    </Button>}
                </div>
              </div>

              {avatarSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-2608">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-2609" />
                  <span className="font-medium" data-id="element-2610">
                    Foto de perfil actualizada correctamente
                  </span>
                </div>}

              {profileSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-2611">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-2612" />
                  <span className="font-medium" data-id="element-2613">
                    Perfil actualizado correctamente
                  </span>
                </div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2614">
                <div className="space-y-5" data-id="element-2615">
                  <Input label="Nombre Completo" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" disabled={!canEditProfile} data-id="element-2616" />
                  <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" disabled={!canEditProfile} data-id="element-2617" />
                </div>
                <div className="space-y-5" data-id="element-2618">
                  <Input label="Cargo / Rol" value={user?.role || 'Administrador'} disabled className="bg-gray-50 dark:bg-gray-900/50" data-id="element-2619" />
                  {canEditProfile && <div className="pt-7" data-id="element-2620">
                      <Button onClick={handleSaveProfile} className="w-full" data-id="element-2621">
                        Guardar Cambios
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'preferences',
    label: 'Preferencias',
    icon: SettingsIcon,
    permissionKey: 'settings_preferences' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2622">
          <div className="space-y-6" data-id="element-2623">
            {!canEditPreferences && <ReadOnlyBanner data-id="element-2624" />}
            <div data-id="element-2625">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-2626">
                Preferencias del Sistema
              </h3>
              <div className="space-y-6" data-id="element-2627">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-2628">
                  <div data-id="element-2629">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-2630">
                      Modo Oscuro
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2631">
                      Activar tema oscuro para reducir la fatiga visual
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2632">
                    <input type="checkbox" checked={preferences.darkMode} onChange={e => handleToggleDarkMode(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-2633" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-2634"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-2635">
                  <div data-id="element-2636">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-2637">
                      Notificaciones de Escritorio
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2638">
                      Recibir alertas emergentes en el navegador
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2639">
                    <input type="checkbox" checked={preferences.desktopNotifications} onChange={e => handleToggleDesktopNotifications(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-2640" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-2641"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: BellIcon,
    permissionKey: 'settings_notifications' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2642">
          <div className="space-y-6" data-id="element-2643">
            {!canEditNotifications && <ReadOnlyBanner data-id="element-2644" />}
            <div data-id="element-2645">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-2646">
                Configuración de Notificaciones
              </h3>
              <div className="space-y-4" data-id="element-2647">
                {[{
              key: 'newAppointments',
              label: 'Nuevas Citas',
              desc: 'Notificar cuando se crea una nueva cita'
            }, {
              key: 'cancelledAppointments',
              label: 'Citas Canceladas',
              desc: 'Notificar cuando se cancela una cita'
            }, {
              key: 'newPatients',
              label: 'Nuevos Pacientes',
              desc: 'Notificar cuando se registra un nuevo paciente'
            }].map(item => <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-2648">
                    <div data-id="element-2649">
                      <p className="font-semibold text-gray-900 dark:text-white" data-id="element-2650">
                        {item.label}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2651">
                        {item.desc}
                      </p>
                    </div>
                    <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2652">
                      <input type="checkbox" checked={notifications[item.key as keyof NotificationSettings]} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  [item.key]: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-2653" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-2654"></div>
                    </label>
                  </div>)}
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'security',
    label: 'Seguridad',
    icon: ShieldIcon,
    permissionKey: 'settings_security' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2655">
          <div className="space-y-6" data-id="element-2656">
            {!canEditSecurity && <ReadOnlyBanner data-id="element-2657" />}
            <div data-id="element-2658">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-2659">
                Seguridad de la Cuenta
              </h3>

              {passwordSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-2660">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-2661" />
                  <span className="font-medium" data-id="element-2662">
                    Contraseña actualizada correctamente
                  </span>
                </div>}

              {passwordError && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center gap-2" data-id="element-2663">
                  <ShieldIcon className="w-5 h-5" data-id="element-2664" />
                  <span className="font-medium" data-id="element-2665">{passwordError}</span>
                </div>}

              {canEditSecurity ? <div className="space-y-5 max-w-lg" data-id="element-2666">
                  <Input label="Contraseña Actual" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-2667" />
                  <Input label="Nueva Contraseña" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-2668" />
                  <Input label="Confirmar Nueva Contraseña" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-2669" />
                  <div className="pt-2" data-id="element-2670">
                    <Button onClick={handleChangePassword} isLoading={isChangingPassword} data-id="element-2671">
                      Cambiar Contraseña
                    </Button>
                  </div>
                </div> : <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 text-center" data-id="element-2672">
                  <ShieldIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" data-id="element-2673" />
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-2674">
                    No tienes permisos para cambiar la contraseña. Contacta a un
                    administrador.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
  }, {
    id: 'email',
    label: 'Correos',
    icon: MailIcon,
    permissionKey: 'settings_email' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2675">
          <EmailNotificationsConfig data-id="element-2676" />
        </div>
  }, {
    id: 'users',
    label: 'Usuarios',
    icon: UsersIcon,
    permissionKey: 'settings_users' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2677">
          <UserManagement data-id="element-2678" />
        </div>
  }, {
    id: 'roles-permissions',
    label: 'Roles y Permisos',
    icon: KeyIcon,
    permissionKey: 'settings_roles_permissions' as const,
    content: <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2679">
          <Roles data-id="element-2680" />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8" data-id="element-2681">
            <Permissions data-id="element-2682" />
          </div>
        </div>
  }];
  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => hasPermission(userRole, tab.permissionKey, 'read'));
  // Set default tab if current is invalid
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTabId)) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);
  const activeTabContent = tabs.find(t => t.id === activeTabId)?.content;
  return <div className="flex flex-col gap-6 h-[calc(100vh-6rem)]" data-id="element-2683">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1BACA6] to-[#1890C8] p-8 text-white shadow-lg shrink-0" data-id="element-2684">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2685" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2686">
          <div data-id="element-2687">
            <div className="flex items-center gap-3 mb-2" data-id="element-2688">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl shadow-inner" data-id="element-2689">
                <SettingsIcon className="w-6 h-6" data-id="element-2690" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight" data-id="element-2691">
                Configuración
              </h1>
            </div>
            <p className="text-teal-50/90 max-w-xl text-lg font-light" data-id="element-2692">
              Administra tu cuenta, preferencias y seguridad del sistema
            </p>
          </div>
        </div>
      </div>

      {tabs.length > 0 ? <div className="flex flex-col lg:flex-row gap-8 flex-1 min-h-0" data-id="element-2693">
          {/* Sidebar Navigation */}
          <nav className="w-full lg:w-72 shrink-0 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 px-1" data-id="element-2694">
            {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTabId === tab.id;
          return <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={`
                    flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200
                    whitespace-nowrap lg:whitespace-normal
                    ${isActive ? 'bg-white dark:bg-gray-800 text-[#1BACA6] shadow-md border border-gray-100 dark:border-gray-700 translate-x-1' : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'}
                  `} data-id="element-2695">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-[#1BACA6]' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-2696" />
                  <span className="flex-1 text-left" data-id="element-2697">{tab.label}</span>
                  {isActive && <ChevronRightIcon className="w-4 h-4 opacity-50 hidden lg:block" data-id="element-2698" />}
                </button>;
        })}
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto pr-2 pb-10 custom-scrollbar" data-id="element-2699">
            {activeTabContent}
          </div>
        </div> : <Card data-id="element-2700">
          <div className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-2701">
            <SettingsIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-2702" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2703">
              Acceso Restringido
            </h3>
            <p data-id="element-2704">No tienes permisos para ver ninguna sección de configuración.</p>
          </div>
        </Card>}
    </div>;
}